import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../header';
import Footer from '../../footer';
import { HiOutlineShoppingBag, HiCheckCircle, HiOutlineClock } from "react-icons/hi";

// Mock Data for Order History
const mockOrders = [
    {
        id: 'ORD-73921',
        token: 'RB-8X91P',
        date: 'Aug 30, 2026',
        total: 310,
        status: 'Completed',
        method: 'UPI',
        items: [
            { name: 'Royal Pista Kulfi', quantity: 2, price: 60 },
            { name: 'Alphonso Mango Kulfi', quantity: 3, price: 60 },
            { name: 'Classic Malai Kulfi', quantity: 1, price: 50 },
        ]
    },
    {
        id: 'ORD-64012',
        token: 'RB-K2M9X',
        date: 'Aug 25, 2026',
        total: 130,
        status: 'Completed',
        method: 'Cash on Delivery',
        items: [
            { name: 'Double Chocolate Kulfi', quantity: 2, price: 65 },
        ]
    }
];

export default function OrderHistory() {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    
    useEffect(() => {
        // Redirect if not logged in
        if (localStorage.getItem('isLoggedIn') !== 'true') {
            navigate('/login', { state: { from: { pathname: '/orders' } } });
            return;
        }
        
        // Fetch from local storage and combine with mock data
        const savedOrders = JSON.parse(localStorage.getItem('orderHistory') || '[]');
        setOrders([...savedOrders, ...mockOrders]);
    }, [navigate]);

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
            <Header />

            <main className="flex-1 pt-[130px] sm:pt-[160px] pb-24 px-6 relative z-10 max-w-5xl mx-auto w-full">
                <div className="mb-12 text-center sm:text-left">
                    <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#EAF8F5] text-[#73C8BD] text-[12px] font-extrabold tracking-wider uppercase mb-3 border border-[#73C8BD]/20">
                        <HiOutlineShoppingBag className="text-sm" />
                        <span>Your Activity</span>
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight">Order History</h1>
                    <p className="text-slate-500 font-medium text-base sm:text-lg">View, track, and manage your recent kulfi bookings and receipts.</p>
                </div>

                {orders.length === 0 ? (
                    <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100 flex flex-col items-center">
                        <HiOutlineShoppingBag className="text-6xl text-slate-200 mb-4" />
                        <h3 className="text-xl font-bold text-slate-900 mb-2">No orders yet</h3>
                        <p className="text-slate-500 mb-6">You haven't placed any orders with us yet.</p>
                        <button onClick={() => navigate('/products')} className="bg-[#181F38] text-white px-8 py-3 rounded-full font-bold hover:bg-slate-800 transition-colors shadow-lg">
                            Start Shopping
                        </button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-6">
                        {orders.map(order => (
                            <div key={order.id} className="bg-slate-50 rounded-[24px] p-6 sm:p-8 border border-slate-100 relative overflow-hidden text-left shadow-sm hover:shadow-md transition-shadow">
                                {/* Decor */}
                                <div className="absolute top-0 right-0 w-32 h-32 bg-[#73C8BD] opacity-10 rounded-full blur-xl translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                                <div className="absolute bottom-0 left-0 w-32 h-32 bg-emerald-500 opacity-10 rounded-full blur-xl -translate-x-1/2 translate-y-1/2 pointer-events-none" />
                                
                                <div className="relative z-10">
                                    {/* Order Header / Ticket Details */}
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                                        <div>
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Priority Token</div>
                                            <div className="text-2xl font-black text-slate-900 tracking-wider mb-2">{order.token}</div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-bold text-slate-500">{order.id}</span>
                                                <span className="text-slate-300">•</span>
                                                <span className="text-sm text-slate-500 flex items-center gap-1">
                                                    <HiOutlineClock />
                                                    {order.date}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-left sm:text-right">
                                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Amount</div>
                                            <div className="text-2xl font-black text-[#73C8BD] mb-2">₹{order.total}</div>
                                            <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-md inline-flex items-center gap-1">
                                                <HiCheckCircle />
                                                {order.status}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Order Items */}
                                    <div className="border-t border-dashed border-slate-300 pt-6 pb-6 mb-6">
                                        <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Items Ordered</h4>
                                        <div className="flex flex-col gap-4">
                                            {order.items.map((item, i) => (
                                                <div key={i} className="flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-white border border-slate-100 text-[#73C8BD] font-bold flex items-center justify-center text-xs shadow-sm">
                                                            x{item.quantity}
                                                        </div>
                                                        <span className="font-bold text-slate-700">{item.name}</span>
                                                    </div>
                                                    <span className="font-bold text-slate-900">₹{item.price * item.quantity}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Footer / Barcode */}
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-slate-500">Payment Method:</span>
                                            <span className="font-bold text-slate-900">{order.method}</span>
                                        </div>
                                        
                                        {/* Fake Barcode */}
                                        <div className="h-10 w-full sm:w-48 bg-slate-200/50 rounded flex overflow-hidden opacity-60">
                                            {[...Array(30)].map((_, i) => (
                                                <div key={i} className="h-full bg-slate-700" style={{ width: `${Math.random() * 4 + 1}px`, marginRight: `${Math.random() * 3}px` }} />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
}
